import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Star, Settings2, Folder } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useProfile, moduleEnabled } from "@/context/BusinessProfileContext";
import { SPACES, visibleItems } from "@/lib/spaces";
import { useDashPrefs } from "@/lib/dashboardPrefs";
import DashboardCustomizer from "@/components/DashboardCustomizer";

const CC = {
  cyan: "bg-cyan-100 text-cyan-600", violet: "bg-violet-100 text-violet-600", green: "bg-emerald-100 text-emerald-600",
  orange: "bg-orange-100 text-orange-600", yellow: "bg-yellow-100 text-yellow-600", pink: "bg-pink-100 text-pink-600", blue: "bg-blue-100 text-blue-600",
};
export const spaceColor = (c) => CC[c] || CC.green;
export const folderIcon = (f) => SPACES.find((s) => s.key === f.icon)?.icon || Folder;

export default function MesEspaces() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { modules } = useProfile();
  const { prefs, save, reset } = useDashPrefs();
  const [cust, setCust] = useState(false);
  if (!prefs) return null;
  const folders = prefs.folders.filter((f) => !f.hidden && visibleItems(f, modules, user, moduleEnabled).length > 0);
  const favs = visibleItems({ items: prefs.favorites }, modules, user, moduleEnabled);
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-slate-700">📁 Mes espaces</div>
        <button data-testid="dash-customize" onClick={() => setCust(true)} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 rounded-full border border-slate-200 bg-white px-3 py-1.5"><Settings2 className="w-3.5 h-3.5" /> Personnaliser</button>
      </div>
      {favs.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="dash-favorites">
          {favs.map((f, i) => <button key={`${f.to}-${i}`} data-testid={`fav-${i}`} onClick={() => navigate(f.to)} className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1.5 text-xs font-medium hover:bg-amber-100"><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-500" />{f.label}</button>)}
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="mes-espaces">
        {folders.map((s) => { const Icon = folderIcon(s); return (
          <button key={s.id} onClick={() => navigate(`/espaces/${s.id}`)} data-testid={`space-${s.id}`}
            className="group bg-white rounded-2xl p-4 text-left border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col sm:flex-row items-start gap-2.5 sm:gap-3 min-w-0 min-h-[64px]">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${spaceColor(s.c)}`}><Icon className="w-5 h-5" /></div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] sm:text-sm font-semibold text-slate-900 leading-tight break-words">{s.label}</div>
              {s.desc && <div className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{s.desc}</div>}
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 shrink-0 mt-1 hidden sm:block" />
          </button>
        ); })}
      </div>
      {cust && <DashboardCustomizer open={cust} onClose={() => setCust(false)} prefs={prefs} save={save} reset={reset} />}
    </div>
  );
}
